import { DatePicker, Flex, Table } from 'antd'
import dayjs from 'dayjs'
import { Dispatch, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useTreatmentServiceGetTreatmentPlanRecipeListEndpoint } from 'src/api/queries'
import { RecipeSortBy, RecipeStatus } from 'src/api/requests'
import { Select, Text } from 'src/components'
import {
  useRecipesColumns,
  useRecipesConfig
} from 'src/features/persons/components'
import { useStyles } from 'src/features/persons/styles'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import {
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

export const PersonRecipesPage = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { id: personId } = useParams()

  const pagination = useTablePagination()

  const { options } = useRecipesConfig()
  const { columns } = useRecipesColumns()

  const [dateIssued, setDateIssued] = useState('')
  const [dateDispensed, setDateDispensed] = useState('')
  const [status, setStatus] = useState('')
  const [ordering, setOrdering] = useState('')

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(ordering && {
      sortBy: normalizeOrdering(ordering) as RecipeSortBy,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(dateIssued && {
      dateIssued
    }),
    ...(dateDispensed && {
      dispensingDate: dateDispensed
    }),
    ...(status && { status: status as RecipeStatus }),
    personId
  }

  const { data, isLoading } =
    useTreatmentServiceGetTreatmentPlanRecipeListEndpoint(queryParams)

  const onFilterChange = (
    val: string | dayjs.Dayjs,
    setState: Dispatch<string>
  ) => {
    if (dayjs.isDayjs(val)) {
      setState(val.format(yearMonthDaySlash))
    } else {
      setState(val)
    }
    resetPagination(pagination)
  }

  const recipeStatusOptions = Object.entries(RecipeStatus).map(
    ([key, value]) => ({
      label: t(`recipes.${key}`.toLowerCase()),
      value
    })
  )

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" wrap="wrap" gap={8}>
        <Text className={styles.text}>{t('menu.recipes')}</Text>

        <Flex gap={8}>
          <DatePicker
            format={dayMonthYearBackSlash}
            className="full-width"
            placeholder={t('recipes.date_issued')}
            onChange={(val) => onFilterChange(val, setDateIssued)}
          />

          <DatePicker
            format={dayMonthYearBackSlash}
            className="full-width"
            placeholder={t('recipes.date_dispensing')}
            onChange={(val) => onFilterChange(val, setDateDispensed)}
          />

          <Select
            onSelect={(val) => onFilterChange(val, setStatus)}
            options={recipeStatusOptions}
          />

          <Select
            onSelect={(val) => onFilterChange(val, setOrdering)}
            options={options}
          />
        </Flex>
      </Flex>

      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={data?.data}
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </Flex>
  )
}
